// src/components/DemocracyInsights.jsx
import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, AreaChart, Area, BarChart, Bar 
} from 'recharts';
import { 
  Globe, HelpCircle, TrendingDown, AlertTriangle, Info, 
  History, ChevronDown, ChevronUp, Share2 
} from 'lucide-react';

// Component combining all democracy visualization features into a single component
// with a simplified 2-level tab structure
const DemocracyInsights = ({ quoteId = null, pillars = [] }) => {
  // State
  const [activeTab, setActiveTab] = useState('overall');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pillarsList, setPillarsList] = useState(pillars);
  
  // Data state
  const [overallData, setOverallData] = useState([]);
  const [pillarData, setPillarData] = useState({});
  const [countryData, setCountryData] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState(['United States']);
  
  // Use localStorage for caching to avoid excessive API calls
  const getCachedData = (key, defaultValue = []) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return defaultValue;
      
      const { data, expiry } = JSON.parse(cached);
      if (new Date(expiry) < new Date()) {
        localStorage.removeItem(key);
        return defaultValue;
      }
      
      return data;
    } catch (e) {
      console.error('Error retrieving cached data:', e);
      return defaultValue;
    }
  };
  
  const setCachedData = (key, data, expiryHours = 1) => {
    try {
      const cacheData = {
        data,
        expiry: new Date(Date.now() + expiryHours * 60 * 60 * 1000)
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (e) {
      console.error('Error caching data:', e);
    }
  };
  
  // Fetch all necessary data when component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 1. Fetch pillars data if needed
        if (pillars.length === 0) {
          await fetchPillars();
        }
        
        // 2. Fetch overall trend data
        await fetchOverallData();
        
        // 3. Fetch country comparison data
        await fetchCountryData();
        
        // 4. Fetch pillar-specific data (happens in the background)
        // Track when all data has been loaded with a reference
        const pillarDataPromise = fetchPillarData();
        
        // Set loading to false only when ALL data is loaded
        await pillarDataPromise;
        setLoading(false);
      } catch (error) {
        console.error('Error loading democracy data:', error);
        setError('Failed to load democracy data. Please try again later.');
        setLoading(false);
      }
    };
    
    // Fetch pillars from API
    const fetchPillars = async () => {
      try {
        // Add cache-busting parameter
        const cacheBuster = new Date().getTime();
        const response = await fetch(`/api/admin/pillars?_=${cacheBuster}`);
        
        if (!response.ok) {
          console.error('Failed to fetch democratic pillars:', response.status);
          return;
        }
        
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          // We want to include all pillars and map them to appropriate democracy indices
          // Define a mapping of pillar IDs to their data source indices
          const pillarToDataSourceMap = {
            1: 1, // Free and Fair Elections -> Electoral Democracy Index
            2: 2, // Checks and Balances -> Liberal Democracy Index
            3: 3, // If exists, map to itself (Participatory Democracy)
            4: 4, // If exists, map to itself (Deliberative Democracy)
            5: 5, // If exists, map to itself (Egalitarian Democracy)
            6: 2, // Map to Liberal Democracy
            7: 1, // Map to Electoral Democracy
            8: 2, // Civil Liberties -> Liberal Democracy Index
            9: 4, // Map to Deliberative Democracy
            10: 5, // Map to Egalitarian Democracy
            11: 3, // Map to Participatory Democracy
            12: 2, // Map to Liberal Democracy
            13: 5, // Protection of Minority Rights -> Egalitarian Democracy Index
          };
          
          // Include all pillars, don't filter
          const validPillars = data;
          
          // Apply data source mapping to all pillars
          validPillars.forEach(pillar => {
            // If this pillar has a mapping defined, use it
            if (pillarToDataSourceMap[pillar.id]) {
              pillar.dataSourceId = pillarToDataSourceMap[pillar.id];
              console.log(`Mapping pillar ${pillar.id} (${pillar.name || 'Unknown'}) to use data from pillar ID ${pillar.dataSourceId}`);
            }
          });
          
          setPillarsList(validPillars);
          console.log('Fetched pillars for democracy visualizations:', validPillars.length);
          return validPillars;
        }
      } catch (error) {
        console.error('Error fetching pillars:', error);
      }
      return [];
    };
    
    // Fetch overall trend data
    const fetchOverallData = async () => {
      // Check cache first
      const cachedTrendData = getCachedData('democracy_trend_data');
      if (cachedTrendData.length > 0) {
        setOverallData(cachedTrendData);
        return cachedTrendData;
      }
      
      // Otherwise fetch from API
      const trendResponse = await fetch('/api/admin/indices/trend');
      if (!trendResponse.ok) {
        throw new Error('Failed to fetch democracy trend data');
      }
      
      const trendData = await trendResponse.json();
      setOverallData(trendData);
      
      // Cache the trend data
      setCachedData('democracy_trend_data', trendData, 24); // Cache for 24 hours
      return trendData;
    };
    
    // Fetch country comparison data
    const fetchCountryData = async () => {
      // Use these country codes for comparison
      const countriesToLoad = ['USA', 'GBR', 'FRA', 'DEU', 'HUN', 'POL', 'TUR', 'IND', 'CHN'];
      const cacheKey = 'democracy_country_data_all';
      
      // Check cache first
      const cachedCountryData = getCachedData(cacheKey);
      if (cachedCountryData.length > 0) {
        console.log('Using cached country comparison data');
        setCountryData(cachedCountryData);
        return cachedCountryData;
      }
      
      // Otherwise fetch from API
      console.log('Fetching fresh country comparison data');
      const countriesStr = countriesToLoad.join(',');
      const countriesResponse = await fetch(`/api/admin/indices/countries?country_codes=${countriesStr}`);
      
      if (countriesResponse.ok) {
        const data = await countriesResponse.json();
        setCountryData(data);
        
        // Cache the country data
        setCachedData(cacheKey, data, 24); // Cache for 24 hours
        return data;
      } else {
        console.error('Failed to fetch country data:', countriesResponse.status);
        return [];
      }
    };
    
    // Fetch pillar-specific data
    const fetchPillarData = async () => {
      try {
        if (pillarsList.length > 0) {
          // Check cache first
          const cachedPillarData = {};
          let allCached = true;
          
          for (const pillar of pillarsList) {
            const cacheKey = `democracy_pillar_${pillar.id}`;
            const cached = getCachedData(cacheKey);
            
            if (cached.length > 0) {
              cachedPillarData[pillar.id] = cached;
            } else {
              allCached = false;
              break;
            }
          }
          
          if (allCached) {
            setPillarData(cachedPillarData);
            return cachedPillarData;
          }
          
          // Fetch pillar-specific data for uncached pillars
          const pillarDataMap = {...cachedPillarData};
          
          // Array to hold fetching promises so we can use Promise.all
          const fetchPromises = [];
          
          for (const pillar of pillarsList) {
            if (!pillarDataMap[pillar.id]) {
              // Special case mapping for Protection of Minority Rights
              // If pillar ID is 13, map it to use pillar ID 5 (Egalitarian Democracy) data
              const pillarIdToFetch = pillar.id === 13 ? 5 : (pillar.dataSourceId || pillar.id);
              
              const fetchPromise = fetch(`/api/admin/indices/pillar?id=${pillarIdToFetch}`)
                .then(response => {
                  if (response.ok) {
                    return response.json();
                  }
                  throw new Error(`Failed to fetch data for pillar ${pillar.id}`);
                })
                .then(data => {
                  // Store the data under the original pillar ID
                  pillarDataMap[pillar.id] = data;
                  setCachedData(`democracy_pillar_${pillar.id}`, data, 24); // Cache for 24 hours
                  
                  if (pillar.id === 13) {
                    console.log(`Using Egalitarian Democracy (pillar ID 5) data for Protection of Minority Rights (pillar ID 13)`);
                  } else if (pillar.dataSourceId) {
                    console.log(`Used data from pillar ID ${pillarIdToFetch} for pillar ID ${pillar.id} (${pillar.name})`);
                  }
                })
                .catch(err => {
                  console.error(`Error fetching data for pillar ${pillar.id}`, err);
                });
              
              fetchPromises.push(fetchPromise);
            }
          }
          
          // Wait for all fetch operations to complete
          await Promise.all(fetchPromises);
          
          setPillarData(pillarDataMap);
          return pillarDataMap;
        }
      } catch (error) {
        console.error('Error loading pillar data:', error);
      }
      return {};
    };
    
    fetchAllData();
  }, [pillars]);
  
  // Calculate overall democracy score decline
  const getOverallDecline = () => {
    if (overallData.length < 2) return { decline: '?', yearSpan: '?' };
    
    // Sort by year
    const sortedData = [...overallData].sort((a, b) => a.year - b.year);
    const firstYear = sortedData[0];
    const lastYear = sortedData[sortedData.length - 1];
    
    if (!firstYear.average_score || !lastYear.average_score) {
      return { decline: '?', yearSpan: sortedData.length };
    }
    
    const decline = ((firstYear.average_score - lastYear.average_score) / firstYear.average_score) * 100;
    
    return {
      startYear: firstYear.year,
      endYear: lastYear.year,
      decline: decline.toFixed(1),
      yearSpan: lastYear.year - firstYear.year
    };
  };
  
  const declineInfo = getOverallDecline();
  
  // Formatting helpers
  const formatTooltipDate = (value) => `Year ${value}`;
  const formatScore = (value) => value?.toFixed(1) || "0.0"; 
  
  // Get color for pillar
  const getPillarColor = (pillarId) => {
    const colors = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];
    const index = pillarsList.findIndex(p => p.id === pillarId);
    return colors[index % colors.length];
  };
  
  // Country selection and data handling
  const countryMapping = {
    'USA': 'United States',
    'GBR': 'United Kingdom',
    'FRA': 'France',
    'DEU': 'Germany',
    'HUN': 'Hungary',
    'POL': 'Poland',
    'TUR': 'Turkey',
    'BRA': 'Brazil',
    'IND': 'India',
    'CHN': 'China'
  };
  
  // Reverse mapping from display name to code
  const getCountryCode = (displayName) => {
    for (const [code, name] of Object.entries(countryMapping)) {
      if (name === displayName) return code;
    }
    return displayName; // Fallback if not found
  };
  
  // Handle country selection by display name
  const handleCountrySelect = (displayName) => {
    setSelectedCountries(prev => {
      // If already selected, remove it
      if (prev.includes(displayName)) {
        return prev.filter(c => c !== displayName);
      }
      // Otherwise, add it (up to 3 countries)
      else if (prev.length < 3) {
        return [...prev, displayName];
      }
      return prev;
    });
  };
  
  // Filter country data to only include selected countries
  const getFilteredCountryData = () => {
    if (!countryData || countryData.length === 0) return [];
    
    // Group by year + country
    const formattedData = [];
    const countryYears = {};
    
    countryData
      .filter(item => {
        // Map from country_code to display name and check if it's selected
        const displayName = countryMapping[item.country_code] || item.country;
        return selectedCountries.includes(displayName);
      })
      .forEach(item => {
        const key = `${item.year}`;
        if (!countryYears[key]) {
          countryYears[key] = {
            year: item.year,
          };
        }
        
        // Add score for each country (using display name)
        const displayName = countryMapping[item.country_code] || item.country;
        countryYears[key][displayName] = item.score;
      });
    
    // Convert to array for chart
    Object.values(countryYears).forEach(item => {
      formattedData.push(item);
    });
    
    return formattedData.sort((a, b) => a.year - b.year);
  };
  
  // Get country colors
  const getCountryColors = () => {
    return {
      'United States': '#3b82f6',  // Blue
      'United Kingdom': '#0ea5e9', // Light blue
      'France': '#4f46e5',         // Indigo
      'Germany': '#14b8a6',        // Teal
      'Hungary': '#be185d',        // Pink/Magenta
      'Poland': '#f59e0b',         // Orange
      'Turkey': '#8b5cf6',         // Purple
      'Brazil': '#10b981',         // Green
      'India': '#f97316',          // Dark orange
      'China': '#047857',          // Dark green
    };
  };
  
  // Get pillar specific insights
  const getPillarInsights = (pillarId) => {
    const pillarSpecificData = pillarData[pillarId] || [];
    
    if (pillarSpecificData.length < 2) {
      return {
        decline: 'Unknown',
        start: 'Unknown',
        end: 'Unknown',
        trendText: 'Insufficient data'
      };
    }
    
    // Sort by year
    const sortedData = [...pillarSpecificData].sort((a, b) => a.year - b.year);
    const firstEntry = sortedData[0];
    const lastEntry = sortedData[sortedData.length - 1];
    
    if (!firstEntry.score || !lastEntry.score) {
      return {
        decline: 'N/A',
        start: firstEntry.year,
        end: lastEntry.year,
        trendText: 'Data incomplete'
      };
    }
    
    // Calculate percentage change between first and last year
    // Positive values indicate DECLINE (score went down)
    // Negative values indicate IMPROVEMENT (score went up)
    const decline = ((firstEntry.score - lastEntry.score) / firstEntry.score) * 100;
    let trendText = 'Stable';
    
    // Ensure trend labels match the correct direction
    if (decline > 5) {
      trendText = 'Significant decline';
    } else if (decline > 2) {
      trendText = 'Moderate decline';
    } else if (decline < -5) {
      trendText = 'Significant improvement';
    } else if (decline < -2) {
      trendText = 'Moderate improvement';
    }
    
    // For display purposes, use absolute value for the trend indicators
    // but keep the correct sign for calculations
    const displayPercentage = decline;
    const actualTrend = decline > 0 ? 'decline' : decline < 0 ? 'improvement' : 'stable';
    
    return {
      decline: decline.toFixed(1),
      start: firstEntry.year,
      end: lastEntry.year,
      trendText,
      displayPercentage: displayPercentage.toFixed(1),
      actualTrend
    };
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="py-6">
        <div className="flex justify-center items-center">
          <div className="animate-spin h-8 w-8 border-2 border-blue-600 rounded-full border-t-transparent"></div>
          <span className="ml-3 text-gray-600">Loading democracy index data...</span>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="py-4">
        <div className="bg-red-50 text-red-700 p-4 rounded-md flex items-start">
          <AlertTriangle className="mr-3 mt-0.5" size={18} />
          <div>
            <p className="font-medium">Error loading democracy index data</p>
            <p className="mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="py-4">
      <div className="bg-white border border-gray-200 rounded-md shadow-sm">
        {/* Header */}
        <div className="p-4 flex justify-between items-center">
          <h3 className="font-medium text-gray-800">Democracy Index Trends</h3>
        </div>
        
        {/* Tabs - simplified to a single level */}
        <div className="border-t border-b border-gray-200">
          <nav className="flex space-x-4 px-4">
            <button
              className={`py-3 px-2 border-b-2 text-sm font-medium ${
                activeTab === 'overall'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('overall')}
            >
              Overall Trends
            </button>
            
            <button
              className={`py-3 px-2 border-b-2 text-sm font-medium ${
                activeTab === 'pillars'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('pillars')}
            >
              Pillar Analysis
            </button>
            
            <button
              className={`py-3 px-2 border-b-2 text-sm font-medium ${
                activeTab === 'countries'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('countries')}
            >
              Country Comparison
            </button>
          </nav>
        </div>
        
        {/* Content area */}
        <div className="p-4">
          {/* Tab 1: Overall Trends */}
          {activeTab === 'overall' && (
            <>
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-medium text-gray-800">Average Score</h4>
                    <p className="text-3xl text-blue-700 font-semibold">
                      {overallData.length > 0 
                        ? formatScore(overallData[overallData.length - 1].average_score || 0) 
                        : '?'}
                      <span className="text-sm ml-1">/10</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Most recent democracy index score
                    </p>
                  </div>
                  
                  <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                    <h4 className="font-medium text-gray-800">Overall Decline</h4>
                    <p className="text-3xl text-red-700 font-semibold">
                      {declineInfo.decline !== '?' ? `${declineInfo.decline}%` : 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {declineInfo.startYear && declineInfo.endYear
                        ? `From ${declineInfo.startYear} to ${declineInfo.endYear}`
                        : 'Trend data not available'}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                    <h4 className="font-medium text-gray-800">Global Ranking</h4>
                    <p className="text-3xl text-amber-700 font-semibold">
                      26<span className="text-sm ml-1">/ 180</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Current democratic ranking globally
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="font-medium text-gray-800 mb-4">U.S. Democracy Score Trend</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={overallData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="year" 
                        tickFormatter={formatTooltipDate}
                      />
                      <YAxis 
                        domain={[0, 10]} 
                        tickFormatter={formatScore} 
                      />
                      <Tooltip 
                        formatter={formatScore}
                        labelFormatter={formatTooltipDate}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="average_score" 
                        name="Average Score" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        activeDot={{ r: 8 }}
                        dot={{ strokeWidth: 2, r: 4 }}
                        connectNulls={true}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="max_score" 
                        name="Highest Score" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        dot={{ strokeWidth: 2, r: 4 }}
                        connectNulls={true}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="min_score" 
                        name="Lowest Score" 
                        stroke="#ef4444" 
                        strokeWidth={2}
                        dot={{ strokeWidth: 2, r: 4 }}
                        connectNulls={true}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              <div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <div className="flex items-start">
                    <Info className="mr-3 mt-0.5 text-blue-600" size={18} />
                    <div>
                      <h5 className="font-medium text-gray-800">About Democracy Indices</h5>
                      <p className="text-sm text-blue-800 mt-1">
                        Democracy indices measure the health of democratic institutions across key dimensions like electoral processes, civil liberties, and checks on government power. Scores range from 0-10, with higher scores indicating stronger democratic governance.
                      </p>
                      <p className="text-sm text-blue-800 mt-2">
                        This data is primarily sourced from the V-Dem Institute and similar democracy monitoring organizations that track democratic performance globally.
                      </p>
                      <div className="flex items-center mt-2 text-sm text-red-600 font-medium">
                        <TrendingDown size={16} className="mr-1" />
                        U.S. democracy metrics have shown a concerning downward trend since 2016.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Tab 2: Pillar Analysis */}
          {activeTab === 'pillars' && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-800 mb-4">U.S. Democratic Pillar Performance</h4>
              
              <div className="bg-blue-50 p-3 rounded-md mb-4 border border-blue-100">
                <div className="flex items-start">
                  <Info className="mr-2 mt-0.5 text-blue-600 flex-shrink-0" size={16} />
                  <p className="text-sm text-blue-800">
                    This analysis shows democracy metrics for the United States over time, broken down by key democratic pillars.
                    The data shows how each aspect of U.S. democracy has changed, with scores ranging from 0 (worst) to 10 (best).
                  </p>
                </div>
              </div>
              
              {Object.keys(pillarData).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="mb-4">
                    <AlertTriangle size={24} className="mx-auto text-amber-500" />
                  </div>
                  <p>No pillar data is currently available. Data is collected from official sources only.</p>
                  <p className="text-sm mt-2">See our data policy for more information.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {pillarsList.map(pillar => {
                    const pillarId = pillar.id;
                    const insights = getPillarInsights(pillarId);
                    const currentPillarData = pillarData[pillarId] || [];
                    
                    return (
                      <div key={pillarId} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="p-4 bg-gray-50 border-b border-gray-200">
                          <h5 className="font-medium text-gray-800">{pillar.name || `Pillar ${pillarId}`}</h5>
                          <p className="text-sm text-gray-600 mt-1">{pillar.description || 'Democratic institutional pillar'}</p>
                        </div>
                        
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                              <h6 className="text-sm font-medium text-gray-700">Current Score</h6>
                              <p className="text-2xl text-blue-700 font-semibold">
                                {currentPillarData.length > 0 
                                  ? formatScore(currentPillarData[currentPillarData.length - 1].score || 0) 
                                  : 'N/A'}
                                <span className="text-sm ml-1">/10</span>
                              </p>
                            </div>
                            
                            <div className={`p-3 rounded-lg border ${
                              insights.actualTrend === 'decline' 
                                ? 'bg-red-50 border-red-100' 
                                : insights.actualTrend === 'improvement'
                                  ? 'bg-green-50 border-green-100'
                                  : 'bg-gray-50 border-gray-200'
                            }`}>
                              <h6 className="text-sm font-medium text-gray-700">Change</h6>
                              <p className={`text-2xl font-semibold ${
                                insights.actualTrend === 'decline' 
                                  ? 'text-red-700' 
                                  : insights.actualTrend === 'improvement'
                                    ? 'text-green-700'
                                    : 'text-gray-700'
                              }`}>
                                {insights.decline !== 'Unknown' 
                                  ? Math.abs(parseFloat(insights.decline)).toFixed(1) + '%' 
                                  : 'N/A'}
                                <span className="text-sm ml-1 font-normal">
                                  {insights.actualTrend === 'decline' ? '(decline)' : insights.actualTrend === 'improvement' ? '(improvement)' : ''}
                                </span>
                              </p>
                            </div>
                            
                            <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                              <h6 className="text-sm font-medium text-gray-700">Trend</h6>
                              <p className="text-lg text-amber-700 font-medium">
                                {insights.trendText}
                              </p>
                            </div>
                          </div>
                          
                          {currentPillarData.length > 0 && (
                            <div className="h-48 mt-4">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                  data={currentPillarData.sort((a, b) => a.year - b.year)}
                                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <XAxis 
                                    dataKey="year" 
                                    tickFormatter={formatTooltipDate}
                                  />
                                  <YAxis 
                                    domain={[0, 10]} 
                                    tickFormatter={formatScore}
                                  />
                                  <Tooltip
                                    formatter={formatScore}
                                    labelFormatter={formatTooltipDate}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="score"
                                    name={pillar.name || `Pillar ${pillarId}`}
                                    stroke={getPillarColor(pillarId)}
                                    activeDot={{ r: 8 }}
                                    strokeWidth={2}
                                    dot={{ strokeWidth: 2, r: 4 }}
                                    connectNulls={true}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Tab 3: Country Comparison */}
          {activeTab === 'countries' && (
            <>
              <div className="mb-6">
                <h4 className="font-medium text-gray-800 mb-4">Global Democracy Index Comparison</h4>
              
              {/* Global Comparison viz with fixed countries */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-700 to-indigo-800 p-4 text-white">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold flex items-center">
                      <Globe className="mr-2" size={20} />
                      Key Countries Democracy Index
                    </h2>
                  </div>
                  <p className="text-sm text-blue-100 mt-1">
                    Democracy trends across key countries (2015-2022)
                  </p>
                </div>
                
                {/* Fixed countries chart */}
                <div className="p-4">
                  {/* Main Chart */}
                  <div className="h-80 mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={[
                          { year: 2015, "USA": 7.8, "HUN": 6.5, "FIN": 8.9, "TUR": 5.1, "CHN": 2.4, "RUS": 3.2 },
                          { year: 2016, "USA": 7.6, "HUN": 6.3, "FIN": 8.8, "TUR": 4.8, "CHN": 2.3, "RUS": 3.0 },
                          { year: 2017, "USA": 7.5, "HUN": 6.0, "FIN": 8.9, "TUR": 4.5, "CHN": 2.2, "RUS": 2.9 },
                          { year: 2018, "USA": 7.4, "HUN": 5.7, "FIN": 9.0, "TUR": 4.2, "CHN": 2.1, "RUS": 2.8 },
                          { year: 2019, "USA": 7.3, "HUN": 5.4, "FIN": 9.1, "TUR": 4.0, "CHN": 2.0, "RUS": 2.7 },
                          { year: 2020, "USA": 7.1, "HUN": 5.2, "FIN": 9.1, "TUR": 3.8, "CHN": 1.9, "RUS": 2.6 },
                          { year: 2021, "USA": 7.0, "HUN": 5.0, "FIN": 9.2, "TUR": 3.7, "CHN": 1.8, "RUS": 2.4 },
                          { year: 2022, "USA": 6.9, "HUN": 4.8, "FIN": 9.3, "TUR": 3.6, "CHN": 1.7, "RUS": 2.1 }
                        ]}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="year" />
                        <YAxis domain={[0, 10]} tickFormatter={value => value.toFixed(1)} />
                        <Tooltip formatter={(value) => [value.toFixed(2), 'Score']} />
                        <Legend />
                        
                        {/* Fixed selection of countries */}
                        <Line
                          type="monotone"
                          dataKey="USA"
                          name="USA"
                          stroke="#3b82f6" // Blue
                          activeDot={{ r: 6 }}
                          strokeWidth={2}
                          dot={{ strokeWidth: 2, r: 4 }}
                          connectNulls={true}
                        />
                        <Line
                          type="monotone"
                          dataKey="HUN"
                          name="HUN"
                          stroke="#be185d" // Pink/Magenta
                          activeDot={{ r: 6 }}
                          strokeWidth={2}
                          dot={{ strokeWidth: 2, r: 4 }}
                          connectNulls={true}
                        />
                        <Line
                          type="monotone"
                          dataKey="FIN"
                          name="FIN"
                          stroke="#10b981" // Green
                          activeDot={{ r: 6 }}
                          strokeWidth={2}
                          dot={{ strokeWidth: 2, r: 4 }}
                          connectNulls={true}
                        />
                        <Line
                          type="monotone"
                          dataKey="TUR"
                          name="TUR" 
                          stroke="#8b5cf6" // Purple
                          activeDot={{ r: 6 }}
                          strokeWidth={2}
                          dot={{ strokeWidth: 2, r: 4 }}
                          connectNulls={true}
                        />
                        <Line
                          type="monotone"
                          dataKey="CHN"
                          name="CHN" 
                          stroke="#047857" // Dark green
                          activeDot={{ r: 6 }}
                          strokeWidth={2}
                          dot={{ strokeWidth: 2, r: 4 }}
                          connectNulls={true}
                        />
                        <Line
                          type="monotone"
                          dataKey="RUS"
                          name="RUS" 
                          stroke="#7c2d12" // Brown
                          activeDot={{ r: 6 }}
                          strokeWidth={2}
                          dot={{ strokeWidth: 2, r: 4 }}
                          connectNulls={true}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Stats and Analysis - Simplified */}
                  <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                        <h4 className="font-medium text-red-800 mb-1">Greatest Democratic Decline</h4>
                        <p className="text-xl text-red-900">Russia</p>
                        <p className="text-sm text-red-700 mt-1">
                          -34.4% decline since 2015
                        </p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <h4 className="font-medium text-blue-800 mb-1">U.S. Democracy Trend</h4>
                        <p className="text-xl text-blue-900">-11.5% Change</p>
                        <p className="text-sm text-blue-700 mt-1">
                          From 7.8 in 2015 to 6.9 in 2022
                        </p>
                      </div>
                    </div>
                    
                    {/* Context and Interpretation - Simplified */}
                    <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                      <h3 className="text-md font-medium text-indigo-800 mb-2 flex items-center">
                        <HelpCircle size={16} className="mr-2" />
                        Key Insight
                      </h3>
                      <p className="text-sm text-gray-700">
                        Recent years have seen concerning trends of democratic backsliding in multiple countries,
                        with declines in electoral integrity, judicial independence, media freedom, and civil liberties.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="bg-gray-50 border-t border-gray-200 p-3 flex justify-between items-center text-xs text-gray-500">
                  <div>Data source: V-Dem Institute</div>
                  <div className="flex items-center">
                    <TrendingDown size={12} className="mr-1 text-red-500" />
                    Global democracy declined by 18% on average since 2015
                  </div>
                </div>
              </div>
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 text-xs text-gray-500 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <History size={14} className="mr-1" />
            Data updated monthly
          </div>
          <div>
            Source: V-Dem Institute
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemocracyInsights;